import React from 'react';
import { cn } from '../index';
import { Spinner } from './Spinner';
import { EmptyState } from './EmptyState';

export interface DataTableColumn<T> {
  key: string;
  header: React.ReactNode;
  className?: string;
  render: (item: T) => React.ReactNode;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  data: T[];
  loading?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyIcon?: React.ReactNode;
  onRowClick?: (item: T) => void;
  keyExtractor: (item: T) => string;
  className?: string;
  /**
   * Max height of the scrollable table body area.
   * Pass `false` to disable internal scrolling.
   * @default 'calc(100dvh - 15rem)'
   */
  maxHeight?: string | false;
}

/** Shared viewport-based max height for project grids. */
export const DATA_TABLE_MAX_HEIGHT = 'calc(100dvh - 15rem)';

export function DataTable<T>({
  columns,
  data,
  loading,
  emptyTitle = 'No data found',
  emptyDescription,
  emptyIcon,
  onRowClick,
  keyExtractor,
  className,
  maxHeight = DATA_TABLE_MAX_HEIGHT,
}: DataTableProps<T>) {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-48 gap-3 text-[var(--fg-muted)]">
        <Spinner size="md" />
        <span className="text-sm">Loading...</span>
      </div>
    );
  }

  if (data.length === 0) {
    return <EmptyState icon={emptyIcon} title={emptyTitle} description={emptyDescription} />;
  }

  const scrollable = maxHeight !== false;

  return (
    <div
      className={cn('overflow-auto', className)}
      style={scrollable ? { maxHeight } : undefined}
    >
      <table className="w-full text-sm">
        <thead className={cn(scrollable && 'sticky top-0 z-10')}>
          <tr className="border-b border-[var(--border)]">
            {columns.map((col) => (
              <th
                key={col.key}
                className={cn(
                  'px-3 py-2.5 text-left text-[11px] font-medium text-[var(--fg-muted)] uppercase tracking-wider',
                  scrollable && 'bg-[var(--card)]',
                  col.className,
                )}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--border)]">
          {data.map((item) => (
            <tr
              key={keyExtractor(item)}
              onClick={() => onRowClick?.(item)}
              className={cn(
                'hover:bg-[var(--muted)] transition-colors',
                onRowClick && 'cursor-pointer',
              )}
            >
              {columns.map((col) => (
                <td key={col.key} className={cn('px-3 py-2.5', col.className)}>
                  {col.render(item)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
